// src/components/form/ConsultationForm.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/variables';
import Button from '../common/Button';

const FormContainer = styled.div`
  max-width: ${props => props.isModal ? '100%' : '600px'};
  margin: ${props => props.isModal ? '0' : '0 auto'};
  padding: ${props => props.isModal ? '0' : '24px'};
  background-color: ${colors.white};
  border-radius: ${props => props.isModal ? '0' : '8px'};
  box-shadow: ${props => props.isModal ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
`;

const FormTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: 24px;
  display: ${props => props.isModal ? 'none' : 'block'};
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: ${colors.primary};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  color: ${colors.primary};
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${colors.secondary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  color: ${colors.primary};
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${colors.secondary};
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 24px;
  text-align: center;
`;

const ConsultationForm = ({ onSubmitSuccess, isModal }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    course: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // 입력 시 해당 필드의 에러 메시지 제거
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = '전화번호를 입력해주세요';
    } else if (!/^[0-9]{2,3}-?[0-9]{3,4}-?[0-9]{4}$/.test(formData.phone.replace(/-/g, ''))) {
      newErrors.phone = '유효한 전화번호를 입력해주세요';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '유효한 이메일 주소를 입력해주세요';
    }
    
    if (!formData.course.trim()) {
      newErrors.course = '관심 코스를 입력해주세요';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = '문의 내용을 입력해주세요';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        const response = await fetch('/api/education', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error('이메일 전송에 실패했습니다.');
        }

        const result = await response.json();
        
        if (result.success) {
        setIsSubmitted(true);
        // 폼 초기화
        setFormData({
          name: '',
          phone: '',
          email: '',
            course: '',
          message: ''
        });
        
        // 모달에서 열린 경우 부모에게 알림
        if (onSubmitSuccess && isModal) {
          setTimeout(() => {
            onSubmitSuccess();
            }, 2000);
          }
        } else {
          throw new Error(result.message || '이메일 전송에 실패했습니다.');
        }
      } catch (error) {
        console.error('이메일 전송 오류:', error);
        setErrors({
          submit: '상담 신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  const handleNewSubmission = () => {
    setIsSubmitted(false);
  };
  
  return (
    <FormContainer isModal={isModal}>
      {isSubmitted ? (
        <>
          <SuccessMessage>
            교육상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.
          </SuccessMessage>
          {!isModal && <Button onClick={handleNewSubmission} large>새 문의하기</Button>}
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <FormTitle isModal={isModal}>교육상담 신청</FormTitle>
          
          <FormGroup>
            <Label htmlFor="name">이름</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="홍길동"
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="phone">전화번호</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="010-1234-5678"
            />
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="email">이메일</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@example.com"
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="course">관심 코스</Label>
            <Input
              type="text"
              id="course"
              name="course"
              value={formData.course}
              onChange={handleChange}
              placeholder="예: Java 기초, React.js 등"
            />
            {errors.course && <ErrorMessage>{errors.course}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="message">문의 내용</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="궁금한 점이나 상담 받고 싶은 내용을 자유롭게 작성해주세요."
            />
            {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
          </FormGroup>
          
          {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
          
          <Button type="submit" large disabled={isLoading}>
            {isLoading ? '제출 중...' : '상담 신청하기'}
          </Button>
        </form>
      )}
    </FormContainer>
  );
};

export default ConsultationForm;